import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, UserPlus, Trash2, Lock, Unlock, ShieldCheck, Plus, Pencil, Check, X,
  RefreshCw, User, Clock, CheckCircle2, XCircle, Search, Loader2, Mail, Phone, Building2,
  ChevronDown, ChevronUp, Shield, KeyRound
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ROLE_COLORS = {
  admin: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
  owner: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
  manager: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
  staff: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
  viewer: 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-gray-300',
};

const PERMISSION_GROUPS = {
  'Dashboard': ['view_dashboard'],
  'Invoices': ['view_invoices', 'create_invoice', 'submit_invoice_lhdn', 'cancel_invoice'],
  'Adjustments (CN/DN)': ['view_adjustments', 'manage_adjustments'],
  'Owner Payments': ['view_owner_payments', 'manage_owner_payments'],
  'Bookings': ['view_bookings', 'manage_bookings'],
  'Properties': ['view_properties', 'manage_properties'],
  'Customers': ['view_customers', 'manage_customers'],
  'Integrations': ['view_integrations', 'manage_integrations'],
  'QR e-Invoice': ['view_qr_einvoice', 'manage_qr_einvoice'],
  'Reports': ['view_reports'],
  'Audit Trail': ['view_audit_trail'],
  'Billing': ['view_billing', 'manage_billing'],
  'Settings & Access': ['manage_settings', 'manage_tax_settings', 'manage_users', 'manage_roles'],
};

export default function AccessControl({ token, user, teamUsers, roles, onRefresh }) {
  const [subTab, setSubTab] = useState('users');
  const headers = { Authorization: `Bearer ${token}` };

  // ─── Users Tab State ───
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', role: 'viewer', phone: '', password: '' });
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [linkRole, setLinkRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetting, setResetting] = useState(false);

  // ─── Approvals Tab State ───
  const [allPortalUsers, setAllPortalUsers] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(null);
  const [approveForm, setApproveForm] = useState({ role: 'viewer', property_id: '' });
  const [expandedUser, setExpandedUser] = useState(null);

  // ─── Roles Tab State ───
  const [rbacRoles, setRbacRoles] = useState([]);
  const [rbacPerms, setRbacPerms] = useState([]);
  const [rbacLoading, setRbacLoading] = useState(false);
  const [rbacEditRole, setRbacEditRole] = useState(null);
  const [rbacRoleForm, setRbacRoleForm] = useState({ name: '', description: '', permissions: [] });
  const [rbacSaving, setRbacSaving] = useState(false);
  const [rbacProperties, setRbacProperties] = useState([]);

  // ─── Fetch Approval Users ───
  const fetchApprovalUsers = useCallback(async () => {
    setApprovalsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/einvoice/portal/admin/all-users`, { headers });
      setAllPortalUsers(data.users || []);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load user approvals');
    } finally {
      setApprovalsLoading(false);
    }
  }, [token]);

  // ─── Fetch RBAC Data ───
  const fetchRbacData = useCallback(async () => {
    setRbacLoading(true);
    try {
      const [rolesRes, permsRes, propsRes] = await Promise.all([
        axios.get(`${API_URL}/api/einvoice/portal/rbac/roles`, { headers }).catch(() => ({ data: { roles: [] } })),
        axios.get(`${API_URL}/api/einvoice/portal/rbac/permissions`, { headers }).catch(() => ({ data: { permissions: [] } })),
        axios.get(`${API_URL}/api/einvoice/portal/properties`, { headers }).catch(() => ({ data: { properties: [] } })),
      ]);
      setRbacRoles(rolesRes.data.roles || []);
      setRbacPerms(permsRes.data.permissions || []);
      setRbacProperties(propsRes.data.properties || propsRes.data || []);
    } catch {
      toast.error('Failed to load roles data');
    } finally {
      setRbacLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (subTab === 'approvals') fetchApprovalUsers();
    if (subTab === 'roles') fetchRbacData();
  }, [subTab]);

  // ═══════════════ Users Handlers ═══════════════

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.full_name) { toast.error('Email and name required'); return; }
    if (inviteForm.password && inviteForm.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setInviting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/user-management/invite`, inviteForm, { headers });
      if (data.password_set_by_admin) {
        toast.success('User created. They can now log in with the password you set.', { duration: 8000 });
      } else {
        toast.success(`Invited! Temp password: ${data.temp_password} (copy it now)`, { duration: 15000 });
      }
      setShowInvite(false);
      setInviteForm({ email: '', full_name: '', role: 'viewer', phone: '', password: '' });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to invite'); }
    finally { setInviting(false); }
  };

  useEffect(() => {
    if (!showInvite) return undefined;
    let active = true;
    setSearchingUsers(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/einvoice/user-management/search-platform-users`, { params: { q: userSearch }, headers: { Authorization: `Bearer ${token}` } });
        if (active) setSearchResults(data.users || []);
      } catch (err) { if (active) setSearchResults([]); }
      finally { if (active) setSearchingUsers(false); }
    }, 350);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, showInvite, token]);

  const resetInviteModal = () => {
    setShowInvite(false); setSelectedUser(null); setUserSearch(''); setSearchResults([]); setLinkRole('viewer');
  };

  const handleLinkExisting = async () => {
    if (!selectedUser) { toast.error('Please select a user'); return; }
    setInviting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/user-management/link-existing`, { unified_user_id: selectedUser.id, role: linkRole }, { headers });
      toast.success(data.message || 'User added to your team.');
      resetInviteModal();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add user'); }
    finally { setInviting(false); }
  };

  const handleResetPassword = async () => {
    if (!resetPwd || resetPwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setResetting(true);
    try {
      await axios.put(`${API_URL}/api/einvoice/user-management/users/${resetUser.id}/password`, { password: resetPwd }, { headers });
      toast.success(`Password reset for ${resetUser.full_name}. Share it with them securely.`, { duration: 8000 });
      setResetUser(null);
      setResetPwd('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
    finally { setResetting(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/einvoice/user-management/users/${userId}/role`, { role: newRole }, { headers });
      toast.success('Role updated');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleToggleUser = async (userId, isActive) => {
    try {
      await axios.put(`${API_URL}/api/einvoice/user-management/users/${userId}/status`, { is_active: !isActive }, { headers });
      toast.success(isActive ? 'User disabled' : 'User enabled');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Remove this user permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/einvoice/user-management/users/${userId}`, { headers });
      toast.success('User removed');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ═══════════════ Approval Handlers ═══════════════

  const pendingUsers = allPortalUsers.filter(u => (u.status || '').toUpperCase() === 'PENDING');
  const filteredPending = pendingUsers.filter(u =>
    !searchQuery ||
    (u.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/api/einvoice/portal/admin/approve-user`, {
        user_id: userId,
        role: approveForm.role,
        property_id: approveForm.property_id || undefined,
      }, { headers });
      toast.success('User approved and role assigned');
      setShowApproveModal(null);
      setApproveForm({ role: 'viewer', property_id: '' });
      fetchApprovalUsers();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve user');
    } finally { setActionLoading(null); }
  };

  const handleReject = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/api/einvoice/portal/admin/reject-user`, {
        user_id: userId, reason: rejectReason,
      }, { headers });
      toast.success('User rejected');
      setShowRejectModal(null);
      setRejectReason('');
      fetchApprovalUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject user');
    } finally { setActionLoading(null); }
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try { return new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
  };

  // ═══════════════ Roles Handlers ═══════════════

  const openRoleEditor = (role) => {
    if (role === 'new') {
      setRbacRoleForm({ name: '', description: '', permissions: [] });
      setRbacEditRole('new');
    } else {
      setRbacRoleForm({ name: role.name, description: role.description, permissions: [...(role.permissions || [])] });
      setRbacEditRole(role);
    }
  };

  const togglePerm = (key) => {
    setRbacRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key) ? prev.permissions.filter(p => p !== key) : [...prev.permissions, key],
    }));
  };

  const toggleGroupPerms = (groupPerms) => {
    const allSelected = groupPerms.every(p => rbacRoleForm.permissions.includes(p));
    if (allSelected) {
      setRbacRoleForm(prev => ({ ...prev, permissions: prev.permissions.filter(p => !groupPerms.includes(p)) }));
    } else {
      setRbacRoleForm(prev => ({ ...prev, permissions: [...new Set([...prev.permissions, ...groupPerms])] }));
    }
  };

  const saveRole = async () => {
    if (!rbacRoleForm.name.trim()) { toast.error('Role name is required'); return; }
    setRbacSaving(true);
    try {
      if (rbacEditRole === 'new') {
        await axios.post(`${API_URL}/api/einvoice/portal/rbac/roles`, rbacRoleForm, { headers });
        toast.success('Role created');
      } else {
        await axios.put(`${API_URL}/api/einvoice/portal/rbac/roles/${rbacEditRole.id}`, rbacRoleForm, { headers });
        toast.success('Role updated');
      }
      setRbacEditRole(null);
      fetchRbacData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    } finally { setRbacSaving(false); }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await axios.delete(`${API_URL}/api/einvoice/portal/rbac/roles/${roleId}`, { headers });
      toast.success('Role deleted');
      fetchRbacData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Group permissions from the API data
  const groupedPerms = {};
  rbacPerms.forEach(p => {
    let assigned = false;
    for (const [group, keys] of Object.entries(PERMISSION_GROUPS)) {
      if (keys.includes(p.key)) {
        if (!groupedPerms[group]) groupedPerms[group] = [];
        groupedPerms[group].push(p);
        assigned = true;
        break;
      }
    }
    if (!assigned) {
      if (!groupedPerms['Other']) groupedPerms['Other'] = [];
      groupedPerms['Other'].push(p);
    }
  });

  // ═══════════════ RENDER ═══════════════

  return (
    <div className="space-y-5" data-testid="access-control-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#4169E1] text-lg">Access Control</h3>
          <p className="text-xs text-slate-500 dark:text-gray-400">Manage users, approvals, roles and permissions</p>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
        <TabsList className="bg-slate-100 dark:bg-zinc-800 p-1 rounded-lg w-full sm:w-auto" data-testid="access-control-tabs">
          <TabsTrigger value="users" className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4" data-testid="ac-tab-users">
            <Users className="w-3.5 h-3.5" />Users
          </TabsTrigger>
          <TabsTrigger value="approvals" className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4" data-testid="ac-tab-approvals">
            <Clock className="w-3.5 h-3.5" />Approvals
            {pendingUsers.length > 0 && (
              <span className="ml-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{pendingUsers.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4" data-testid="ac-tab-roles">
            <ShieldCheck className="w-3.5 h-3.5" />Roles & Permissions
          </TabsTrigger>
        </TabsList>

        {/* ════════════ USERS SUB-TAB ════════════ */}
        <TabsContent value="users" className="mt-5">
          <div className="space-y-4" data-testid="users-panel">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 dark:text-gray-400">{teamUsers.length} team member(s)</p>
              <Button size="sm" onClick={() => setShowInvite(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-user-btn">
                <UserPlus className="w-4 h-4 mr-1.5" /> Add User
              </Button>
            </div>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800/50">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Email</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Role</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                      {teamUsers.map(u => (
                        <tr key={u.id} className={`hover:bg-slate-50 dark:hover:bg-zinc-800/50 ${u.is_active === false ? 'opacity-50' : ''}`} data-testid={`user-row-${u.id}`}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                                <User className="w-4 h-4 text-[#4169E1]" />
                              </div>
                              <div>
                                <span className="font-medium text-slate-900 dark:text-white text-sm">{u.full_name}</span>
                                {u.id === user?.id && <Badge variant="outline" className="text-[10px] ml-1.5 py-0">You</Badge>}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-500 dark:text-gray-400 text-sm">{u.email}</td>
                          <td className="py-3 px-4">
                            <Select value={u.role || 'admin'} onValueChange={v => handleRoleChange(u.id, v)} disabled={u.id === user?.id}>
                              <SelectTrigger className="w-[140px] h-7 text-xs" data-testid={`role-select-${u.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {u.role && !roles[u.role] && (
                                  <SelectItem value={u.role}>{u.role} (legacy)</SelectItem>
                                )}
                                {Object.entries(roles).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {u.is_active === false ? (
                              <Badge variant="outline" className="text-[10px] bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800">Disabled</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800">Active</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {u.id !== user?.id && (
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setResetUser(u); setResetPwd(''); }}
                                  data-testid={`reset-password-${u.id}`} title="Reset password">
                                  <KeyRound className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleToggleUser(u.id, u.is_active !== false)}
                                  data-testid={`toggle-user-${u.id}`} title={u.is_active !== false ? 'Disable user' : 'Enable user'}>
                                  {u.is_active !== false ? <Lock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> : <Unlock className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />}
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleRemoveUser(u.id)}
                                  data-testid={`remove-user-${u.id}`} title="Remove user">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {teamUsers.length === 0 && (
                        <tr><td colSpan={5} className="py-10 text-center text-sm text-slate-400 dark:text-gray-500">No team members yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Invite Dialog */}
            <Dialog open={showInvite} onOpenChange={(o) => { if (!o) resetInviteModal(); else setShowInvite(true); }}>
              <DialogContent data-testid="invite-dialog">
                <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-xs text-slate-500 dark:text-gray-400">Search for an existing StayMii user and assign them a role. No new account is created.</p>
                  {!selectedUser ? (
                    <div>
                      <Label className="text-sm">Search User *</Label>
                      <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search by name or email…" data-testid="invite-user-search" autoFocus />
                      <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-slate-200 dark:border-gray-700 divide-y divide-slate-100 dark:divide-gray-800">
                        {searchingUsers ? (
                          <div className="p-3 text-sm text-slate-400 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Searching…</div>
                        ) : searchResults.length === 0 ? (
                          <div className="p-3 text-sm text-slate-400" data-testid="invite-no-results">{userSearch ? 'No matching StayMii users found.' : 'Start typing to search users.'}</div>
                        ) : searchResults.map(u => (
                          <button key={u.id} type="button" onClick={() => setSelectedUser(u)} className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors" data-testid={`invite-user-option-${u.id}`}>
                            <div className="text-sm font-medium text-slate-800 dark:text-gray-100">{u.full_name}</div>
                            <div className="text-xs text-slate-500 dark:text-gray-400">{u.email}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 px-3 py-2.5" data-testid="invite-selected-user">
                      <div>
                        <div className="text-sm font-medium text-slate-800 dark:text-gray-100">{selectedUser.full_name}</div>
                        <div className="text-xs text-slate-500 dark:text-gray-400">{selectedUser.email}</div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)} data-testid="invite-change-user">Change</Button>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm">Role *</Label>
                    <Select value={linkRole} onValueChange={setLinkRole}>
                      <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(roles).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label} - {v.desc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={resetInviteModal}>Cancel</Button>
                  <Button onClick={handleLinkExisting} disabled={inviting || !selectedUser} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-invite-btn">
                    {inviting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />} Add User
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setResetPwd(''); } }}>
              <DialogContent data-testid="reset-password-dialog">
                <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-slate-500 dark:text-gray-400">Set a new password for <span className="font-semibold text-slate-900 dark:text-white">{resetUser?.full_name}</span> ({resetUser?.email}). Share it with them securely.</p>
                  <div>
                    <Label className="text-sm">New Password *</Label>
                    <Input type="text" value={resetPwd} onChange={e => setResetPwd(e.target.value)} placeholder="Min 8 characters" data-testid="reset-password-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setResetUser(null); setResetPwd(''); }}>Cancel</Button>
                  <Button onClick={handleResetPassword} disabled={resetting || resetPwd.length < 8} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-reset-password-btn">
                    {resetting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Reset Password
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        {/* ════════════ APPROVALS SUB-TAB ════════════ */}
        <TabsContent value="approvals" className="mt-5">
          <div className="space-y-4" data-testid="approvals-panel">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500 dark:text-gray-400 shrink-0">{pendingUsers.length} pending approval(s)</p>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-sm" data-testid="approval-search" />
              </div>
              <Button variant="outline" size="sm" onClick={fetchApprovalUsers} disabled={approvalsLoading} className="h-8" data-testid="refresh-approvals-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${approvalsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {approvalsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#4169E1]" /></div>
            ) : filteredPending.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-green-300 dark:text-green-700 mb-3" />
                  <p className="text-sm text-slate-500 dark:text-gray-400">No pending approvals</p>
                  <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">All user registrations have been processed</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="approvals-table">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800/50">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Name</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Email</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Company</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Date</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                        {filteredPending.map(u => (
                          <React.Fragment key={u.id}>
                            <tr className="hover:bg-slate-50 dark:hover:bg-zinc-800/50" data-testid={`approval-row-${u.id}`}>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-700 dark:text-amber-400 font-bold text-xs shrink-0">
                                    {(u.full_name || '?')[0].toUpperCase()}
                                  </div>
                                  <div>
                                    <span className="font-medium text-slate-900 dark:text-white text-sm">{u.full_name || 'Unknown'}</span>
                                    {u.phone && <p className="text-[11px] text-slate-400 dark:text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</p>}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-slate-500 dark:text-gray-400 text-sm">{u.email}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-1">
                                  <span className="text-sm text-slate-700 dark:text-gray-300">{u.company_name || '-'}</span>
                                  <button onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)} className="text-slate-400 hover:text-slate-600 dark:hover:text-gray-300" data-testid={`expand-btn-${u.id}`}>
                                    {expandedUser === u.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-xs text-slate-500 dark:text-gray-400 whitespace-nowrap">{formatDate(u.created_at)}</td>
                              <td className="py-3 px-4">
                                <div className="flex justify-center gap-2">
                                  <Button size="sm" onClick={() => { setShowApproveModal(u); setApproveForm({ role: 'viewer', property_id: '' }); }}
                                    disabled={actionLoading === u.id} className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2.5"
                                    data-testid={`approve-btn-${u.id}`}>
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setShowRejectModal(u.id)}
                                    disabled={actionLoading === u.id} className="border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs h-7 px-2.5"
                                    data-testid={`reject-btn-${u.id}`}>
                                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            {/* Expanded Details */}
                            {expandedUser === u.id && (
                              <tr><td colSpan={5} className="bg-slate-50 dark:bg-zinc-800/30 px-4 py-3" data-testid={`company-details-${u.id}`}>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                  <div><p className="text-slate-400 dark:text-gray-500 mb-0.5">TIN</p><p className="text-slate-700 dark:text-gray-200 font-medium">{u.tin || 'N/A'}</p></div>
                                  <div><p className="text-slate-400 dark:text-gray-500 mb-0.5">BRN</p><p className="text-slate-700 dark:text-gray-200 font-medium">{u.brn || 'N/A'}</p></div>
                                  <div><p className="text-slate-400 dark:text-gray-500 mb-0.5">SST No</p><p className="text-slate-700 dark:text-gray-200 font-medium">{u.sst_number || 'N/A'}</p></div>
                                  <div><p className="text-slate-400 dark:text-gray-500 mb-0.5">MSIC Code</p><p className="text-slate-700 dark:text-gray-200 font-medium">{u.msic_code || 'N/A'}</p></div>
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Approve Modal with Role Assignment */}
            <Dialog open={!!showApproveModal} onOpenChange={() => setShowApproveModal(null)}>
              <DialogContent data-testid="approve-modal">
                <DialogHeader><DialogTitle>Approve User</DialogTitle></DialogHeader>
                {showApproveModal && (
                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-zinc-700">
                      <p className="font-medium text-slate-900 dark:text-white text-sm">{showApproveModal.full_name}</p>
                      <p className="text-xs text-slate-500 dark:text-gray-400">{showApproveModal.email}</p>
                      {showApproveModal.company_name && <p className="text-xs text-slate-400 dark:text-gray-500 mt-1"><Building2 className="w-3 h-3 inline mr-1" />{showApproveModal.company_name}</p>}
                    </div>
                    <div>
                      <Label className="text-sm">Assign Role *</Label>
                      <Select value={approveForm.role} onValueChange={v => setApproveForm({ ...approveForm, role: v })}>
                        <SelectTrigger data-testid="approve-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(roles).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label} - {v.desc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Property Access (optional)</Label>
                      <Select value={approveForm.property_id || 'all'} onValueChange={v => setApproveForm({ ...approveForm, property_id: v === 'all' ? '' : v })}>
                        <SelectTrigger data-testid="approve-property-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Properties</SelectItem>
                          {(Array.isArray(rbacProperties) ? rbacProperties : []).map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Scope user access to a specific property</p>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowApproveModal(null)}>Cancel</Button>
                  <Button onClick={() => handleApprove(showApproveModal?.id)} disabled={actionLoading === showApproveModal?.id}
                    className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-approve-btn">
                    {actionLoading === showApproveModal?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Approve & Assign Role
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Reject Modal */}
            <Dialog open={!!showRejectModal} onOpenChange={() => { setShowRejectModal(null); setRejectReason(''); }}>
              <DialogContent data-testid="reject-modal">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><XCircle className="w-5 h-5 text-red-500" /> Reject User</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 dark:text-gray-300">The user will be notified of the rejection via email.</p>
                  <div>
                    <Label className="text-sm">Reason (optional)</Label>
                    <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g., Incomplete information" data-testid="reject-reason-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowRejectModal(null); setRejectReason(''); }}>Cancel</Button>
                  <Button onClick={() => handleReject(showRejectModal)} disabled={actionLoading === showRejectModal}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-btn">
                    {actionLoading === showRejectModal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Confirm Reject
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        {/* ════════════ ROLES & PERMISSIONS SUB-TAB ════════════ */}
        <TabsContent value="roles" className="mt-5">
          <div className="space-y-5" data-testid="roles-panel">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 dark:text-gray-400">{rbacRoles.length} role(s) defined</p>
              <Button size="sm" onClick={() => openRoleEditor('new')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-role-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Role
              </Button>
            </div>

            {rbacLoading ? (
              <div className="flex justify-center py-10"><RefreshCw className="w-5 h-5 animate-spin text-[#4169E1]" /></div>
            ) : rbacRoles.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-sm text-slate-400 dark:text-gray-500">No roles found. Create one to get started.</CardContent></Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {rbacRoles.map(role => (
                  <Card key={role.id} className="hover:shadow-md transition-shadow" data-testid={`role-card-${role.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                              <Shield className="w-4 h-4 text-[#4169E1]" />
                            </div>
                            <div>
                              <span className="font-semibold text-sm text-slate-800 dark:text-gray-100">{role.name}</span>
                              {role.is_default && <Badge variant="outline" className="text-[10px] ml-1.5 h-4">Default</Badge>}
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-gray-400 mt-1 ml-10">{role.description || 'No description'}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => openRoleEditor(role)} className="h-7 w-7 p-0" data-testid={`edit-role-${role.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-slate-500 dark:text-gray-400" />
                          </Button>
                          {!role.is_default && (
                            <Button size="sm" variant="ghost" onClick={() => deleteRole(role.id)} className="h-7 w-7 p-0" data-testid={`delete-role-${role.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="ml-10 flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[10px] py-0"><Users className="w-3 h-3 mr-1" />{role.user_count || 0} user(s)</Badge>
                        <Badge variant="outline" className="text-[10px] py-0"><ShieldCheck className="w-3 h-3 mr-1" />{(role.permissions || []).length} permission(s)</Badge>
                      </div>
                      <div className="ml-10 flex flex-wrap gap-1 mt-2">
                        {(role.permissions || []).slice(0, 4).map(p => (
                          <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">{p.replace(/_/g, ' ')}</span>
                        ))}
                        {(role.permissions || []).length > 4 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-zinc-700 text-slate-500 dark:text-gray-400">+{role.permissions.length - 4} more</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Role Editor Modal with Grouped Permissions */}
            {rbacEditRole && (
              <Dialog open={!!rbacEditRole} onOpenChange={() => setRbacEditRole(null)}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="role-editor-modal">
                  <DialogHeader>
                    <DialogTitle>{rbacEditRole === 'new' ? 'Create New Role' : `Edit: ${rbacEditRole.name}`}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="text-sm">Role Name *</Label>
                      <Input value={rbacRoleForm.name} onChange={e => setRbacRoleForm({ ...rbacRoleForm, name: e.target.value })} placeholder="e.g., Invoice Manager" data-testid="role-name-input" />
                    </div>
                    <div>
                      <Label className="text-sm">Description</Label>
                      <Input value={rbacRoleForm.description} onChange={e => setRbacRoleForm({ ...rbacRoleForm, description: e.target.value })} placeholder="Brief description of this role" data-testid="role-desc-input" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm">Permissions ({rbacRoleForm.permissions.length} selected)</Label>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setRbacRoleForm({ ...rbacRoleForm, permissions: rbacPerms.map(p => p.key) })} className="text-[11px] text-blue-600 hover:underline">Select all</button>
                          <button type="button" onClick={() => setRbacRoleForm({ ...rbacRoleForm, permissions: [] })} className="text-[11px] text-slate-500 hover:underline">Clear all</button>
                        </div>
                      </div>
                      <div className="space-y-3 max-h-[40vh] overflow-y-auto border rounded-lg p-3 border-slate-200 dark:border-zinc-700">
                        {Object.entries(groupedPerms).length > 0 ? Object.entries(groupedPerms).map(([group, perms]) => {
                          const groupPermKeys = perms.map(p => p.key);
                          const allGroupSelected = groupPermKeys.every(k => rbacRoleForm.permissions.includes(k));
                          const someGroupSelected = groupPermKeys.some(k => rbacRoleForm.permissions.includes(k));
                          return (
                            <div key={group} data-testid={`perm-group-${group.toLowerCase()}`}>
                              <label className="flex items-center gap-2 py-1.5 px-2 rounded bg-slate-50 dark:bg-zinc-800 cursor-pointer font-medium text-sm text-slate-700 dark:text-gray-200 mb-1">
                                <input type="checkbox" checked={allGroupSelected} ref={el => { if (el) el.indeterminate = someGroupSelected && !allGroupSelected; }}
                                  onChange={() => toggleGroupPerms(groupPermKeys)}
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                {group}
                                <span className="text-[10px] text-slate-400 dark:text-gray-500 font-normal ml-auto">{groupPermKeys.filter(k => rbacRoleForm.permissions.includes(k)).length}/{groupPermKeys.length}</span>
                              </label>
                              <div className="ml-6 space-y-0.5">
                                {perms.map(p => (
                                  <label key={p.key} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-slate-50 dark:hover:bg-zinc-800 cursor-pointer" data-testid={`perm-checkbox-${p.key}`}>
                                    <input type="checkbox" checked={rbacRoleForm.permissions.includes(p.key)} onChange={() => togglePerm(p.key)}
                                      className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                    <span className="text-xs text-slate-600 dark:text-gray-300">{p.key.replace(/_/g, ' ')}</span>
                                    {p.description && <span className="text-[10px] text-slate-400 dark:text-gray-500 ml-auto">{p.description}</span>}
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        }) : rbacPerms.map(p => (
                          <label key={p.key} className="flex items-center gap-2.5 py-1.5 px-2 rounded hover:bg-slate-50 dark:hover:bg-zinc-800 cursor-pointer" data-testid={`perm-checkbox-${p.key}`}>
                            <input type="checkbox" checked={rbacRoleForm.permissions.includes(p.key)} onChange={() => togglePerm(p.key)}
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                            <div>
                              <span className="text-sm font-medium text-slate-700 dark:text-gray-200">{p.key.replace(/_/g, ' ')}</span>
                              {p.description && <span className="text-xs text-slate-400 dark:text-gray-500 ml-2">{p.description}</span>}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setRbacEditRole(null)}>Cancel</Button>
                    <Button onClick={saveRole} disabled={rbacSaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-role-btn">
                      {rbacSaving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                      {rbacEditRole === 'new' ? 'Create Role' : 'Save Changes'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
